import React from "react";
import clsx from "clsx";

export interface OcSkeletonTableProps {
  /** Number of skeleton rows to render. Defaults to 5. */
  rows?: number;
  /** Number of columns. Defaults to 5. */
  columns?: number;
  className?: string;
}

/**
 * Animated skeleton loader for table content.
 * Renders gray shimmer bars to indicate loading state.
 */
export function OcSkeletonTable({
  rows = 5,
  columns = 5,
  className,
}: OcSkeletonTableProps) {
  const colWidths = ["w-1/6", "w-1/4", "w-1/5", "w-1/6", "w-1/5"];

  return (
    <div
      className={clsx("w-full overflow-hidden rounded-lg border border-slate-200 dark:border-oc-navy-700", className)}
      aria-label="Loading table data"
      aria-busy="true"
    >
      {/* Header skeleton */}
      <div className="flex items-center gap-4 px-4 py-3 border-b border-slate-200 dark:border-oc-navy-700 bg-white dark:bg-oc-navy-800">
        {Array.from({ length: columns }).map((_, i) => (
          <div
            key={i}
            className={clsx("h-3 oc-skeleton rounded", colWidths[i % colWidths.length])}
          />
        ))}
      </div>

      {/* Row skeletons */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className={clsx(
            "flex items-center gap-4 px-4 py-3 border-b border-slate-200 dark:border-oc-navy-700 last:border-0",
            rowIdx % 2 === 0 ? "bg-slate-50 dark:bg-oc-navy-900" : "bg-slate-100 dark:bg-oc-navy-800/50"
          )}
        >
          {Array.from({ length: columns }).map((_, colIdx) => (
            <div
              key={colIdx}
              className={clsx(
                "h-3.5 oc-skeleton rounded",
                colWidths[colIdx % colWidths.length]
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
